import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "@/lib/api";
import { trackFormStart, trackLead } from "@/lib/analytics";
import { sendEvent, attribution } from "@/lib/tracking";

const GOALS = ["Get fitter", "Get stronger", "Lose weight", "More energy", "Return to training", "Feel better in myself", "Something else"];
const CONTACT = [["phone", "Phone"], ["whatsapp", "WhatsApp"], ["email", "Email"]];

export default function LeadForm({ funnel, submitLabel, extraFields = [], dark = false, testId = "lead-form", showGoal = true }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", mobile: "", email: "", town: "", goal: "", preferred_contact: "phone", consent: false, extra: {} });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [started, setStarted] = useState(false);

  const start = () => {
    if (started) return;
    setStarted(true);
    trackFormStart({ form_name: `${funnel}_lead_form` });
    sendEvent("form_start", { funnel });
  };

  const validate = () => {
    const e = {};
    if (!f.first_name.trim()) e.first_name = "Please add your first name.";
    if (!/^\+?[\d\s()-]{10,16}$/.test(f.mobile.trim())) e.mobile = "Please enter a valid UK mobile number.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = "Please enter a valid email.";
    if (f.town.trim().length < 2) e.town = "Please add your postcode or town.";
    extraFields.forEach((x) => { if (x.required && !(f.extra[x.key] || "").trim()) e[x.key] = "Required."; });
    setErrors(e);
    return !Object.keys(e).length;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (busy || !validate()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...f, funnel, page_source: window.location.pathname, ...attribution() });
      trackLead({ form_name: `${funnel}_lead_form`, enquiry_type: funnel });
      sendEvent("form_submit", { funnel });
      navigate(`/thank-you?name=${encodeURIComponent(f.first_name.trim())}&funnel=${funnel}${data.duplicate ? "&dup=1" : ""}`);
    } catch (err) {
      toast.error(errText(err) || "We couldn't send that just now — please try again.");
    } finally { setBusy(false); }
  };

  const label = `text-[11px] uppercase tracking-[0.22em] ${dark ? "text-kore-light" : "text-kore-forest"}`;
  const input = `mt-2 w-full bg-transparent border-b py-2 focus:outline-none ${dark ? "border-kore-warm/30 focus:border-kore-light text-kore-warm" : "border-kore-ink/25 focus:border-kore-forest text-kore-ink"}`;
  const err = (k) => errors[k] && <span data-testid={`${testId}-error-${k}`} className="block mt-1 text-xs text-red-500">{errors[k]}</span>;

  return (
    <form onSubmit={submit} onFocus={start} noValidate data-testid={testId} className="grid md:grid-cols-2 gap-5">
      <label className="block"><span className={label}>First name</span><input data-testid={`${testId}-first-name`} value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} className={input} autoComplete="given-name" />{err("first_name")}</label>
      <label className="block"><span className={label}>Mobile</span><input data-testid={`${testId}-mobile`} type="tel" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} className={input} autoComplete="tel" />{err("mobile")}</label>
      <label className="block"><span className={label}>Email</span><input data-testid={`${testId}-email`} type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={input} autoComplete="email" />{err("email")}</label>
      <label className="block"><span className={label}>Postcode or town</span><input data-testid={`${testId}-town`} value={f.town} onChange={(e) => setF({ ...f, town: e.target.value })} className={input} autoComplete="postal-code" />{err("town")}</label>
      {showGoal && (
        <label className="block md:col-span-2"><span className={label}>Main goal <span className="opacity-50 normal-case tracking-normal">(optional)</span></span>
          <select data-testid={`${testId}-goal`} value={f.goal} onChange={(e) => setF({ ...f, goal: e.target.value })} className={input}>
            <option value="">Select…</option>{GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select></label>
      )}
      {extraFields.map((x) => (
        <label key={x.key} className={`block ${x.wide ? "md:col-span-2" : ""}`}><span className={label}>{x.label}{!x.required && <span className="opacity-50 normal-case tracking-normal"> (optional)</span>}</span>
          {x.type === "textarea" ? (
            <textarea data-testid={`${testId}-${x.key}`} rows={3} value={f.extra[x.key] || ""} onChange={(e) => setF({ ...f, extra: { ...f.extra, [x.key]: e.target.value } })} className={`${input} resize-none`} />
          ) : x.options ? (
            <select data-testid={`${testId}-${x.key}`} value={f.extra[x.key] || ""} onChange={(e) => setF({ ...f, extra: { ...f.extra, [x.key]: e.target.value } })} className={input}>
              <option value="">Select…</option>{x.options.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : (
            <input data-testid={`${testId}-${x.key}`} value={f.extra[x.key] || ""} onChange={(e) => setF({ ...f, extra: { ...f.extra, [x.key]: e.target.value } })} className={input} />
          )}
          {err(x.key)}</label>
      ))}
      <div className="md:col-span-2">
        <span className={label}>Preferred contact</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {CONTACT.map(([v, l]) => (
            <button type="button" key={v} data-testid={`${testId}-contact-${v}`} onClick={() => setF({ ...f, preferred_contact: v })}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.16em] border transition ${f.preferred_contact === v ? (dark ? "bg-kore-light text-kore-forest border-kore-light" : "bg-kore-forest text-kore-warm border-kore-forest") : (dark ? "border-kore-warm/30 text-kore-warm/80" : "border-kore-ink/25 text-kore-ink/70")}`}>{l}</button>
          ))}
        </div>
      </div>
      <label className={`md:col-span-2 flex items-start gap-3 text-sm ${dark ? "text-kore-warm/70" : "text-kore-ink/70"}`}>
        <input type="checkbox" data-testid={`${testId}-consent`} checked={f.consent} onChange={(e) => setF({ ...f, consent: e.target.checked })} className="mt-1" />
        <span>Keep me posted about KORE news and offers (optional). We'll always reply to your enquiry regardless — see our <a href="/privacy" className="underline">privacy notice</a>.</span>
      </label>
      <button disabled={busy} data-testid={`${testId}-submit`} className={`md:col-span-2 mt-2 w-full justify-center ${dark ? "btn-gold" : "btn-primary"}`}>
        {busy ? "Sending…" : submitLabel} <ArrowUpRight size={14} />
      </button>
    </form>
  );
}
